/**
 * ErrorOverlay — Full-Screen Error State
 *
 * Shown when AppError is set in the store. Covers the entire screen.
 * Two modes:
 *   - Recoverable: show reload button
 *   - Fatal:       show error code + message only
 *
 * All user-facing strings come from UITranslations (market config) so the
 * screen is fully multilingual. German strings are used as fallbacks.
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#screen-5--error-overlay
 */
import type { AppError } from '../../store/app.store';
import type { UITranslations } from '@ims/shared';

interface ErrorOverlayProps {
  error: AppError;
  ui?: UITranslations;
}

export function ErrorOverlay({ error, ui }: ErrorOverlayProps) {
  const perCode: Record<string, { title: string; description: string }> = {
    CONFIG_NOT_FOUND: {
      title:       ui?.errorConfigNotFoundTitle ?? 'Configuration not found',
      description: ui?.errorConfigNotFoundDesc  ?? 'The vehicle configuration could not be loaded.',
    },
    CONFIG_VALIDATION_ERROR: {
      title:       ui?.errorInvalidConfigTitle ?? 'Invalid configuration',
      description: ui?.errorInvalidConfigDesc  ?? 'The configuration file could not be processed.',
    },
    INVALID_CONFIG: {
      title:       ui?.errorInvalidConfigTitle ?? 'Invalid configuration',
      description: ui?.errorInvalidConfigDesc  ?? 'The configuration file could not be processed.',
    },
    ENGINE_CRASH: {
      title:       ui?.errorEngineCrashTitle ?? 'Rendering error',
      description: ui?.errorEngineCrashDesc  ?? 'The 3D experience could not be started.',
    },
    ASSET_LOAD_FAIL: {
      title:       ui?.errorAssetLoadTitle ?? 'Loading error',
      description: ui?.errorAssetLoadDesc  ?? 'One or more assets could not be loaded.',
    },
  };

  const { title, description } = perCode[error.code] ?? {
    title:       ui?.errorFallbackTitle ?? 'Ein Fehler ist aufgetreten',
    description: ui?.errorFallbackDesc  ?? 'Bitte laden Sie die Seite neu.',
  };

  const reloadLabel = ui?.errorReloadButton ?? 'Neu laden';

  function handleReload() {
    window.location.reload();
  }

  return (
    <div
      className="absolute inset-0 z-960 flex flex-col items-center justify-center px-8 gap-8 bg-brand-primary/90 backdrop-blur-[10px]"
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Error code badge */}
      <span className="text-brand-text-secondary text-[10px] tracking-[0.2em] uppercase">
        {error.code}
      </span>

      {/* Message */}
      <div className="text-center space-y-3">
        <h1 className="text-brand-text text-xl font-bold tracking-wide">{title}</h1>
        <p className="text-brand-text-secondary text-sm leading-relaxed max-w-sm">{description}</p>
      </div>

      {/* Reload button — shown for recoverable errors */}
      {error.recoverable && (
        <button
          onClick={handleReload}
          className="h-12 px-10 rounded-brand-btn bg-brand-btn text-brand-text text-xs font-bold tracking-[0.15em] uppercase hover:opacity-90 transition-opacity"
        >
          {reloadLabel}
        </button>
      )}
    </div>
  );
}
